import { useMemo } from "react";
import { useGetEntityData } from "./useGetEntityData";
import { useGetRiskAnalysisBySession } from "./useGetRiskAnalysisBySession";
import {
  getAnalizaRiscStatus,
  getDocumentChecklistStatus,
  getEfectPerturbatorStatus,
  getIdentificareStatus,
} from "@/utils/guideProgressCalculator";
import type {
  DocumentChecklistItem,
  GuideStepStatus,
} from "@/types/guide-step";
import type { EntityData } from "@/types/entity";
import type { RiskAnalysisBySession } from "@/types/risk-analysis";

export type GuideStep = {
  id: number;
  status: GuideStepStatus;
  isVisible: boolean;
};

export type GuideProgressResult = {
  steps: {
    step1: GuideStep;
    step2: GuideStep;
    step3: GuideStep;
    step4: GuideStep;
    step5: GuideStep;
  };
  documentChecklist: DocumentChecklistItem[];
  isLoading: boolean;
  hasSession: boolean;
  entityData: EntityData | null | undefined;
  riskData: RiskAnalysisBySession | undefined;
};

export function useGuideProgress(
  sessionId: string | null | undefined
): GuideProgressResult {
  const { data: entityDataResponse, isLoading: isLoadingEntity } =
    useGetEntityData(sessionId, {
      enabled: !!sessionId,
    });
  const { data: riskData, isLoading: isLoadingRisk } =
    useGetRiskAnalysisBySession(sessionId, {
      enabled: !!sessionId,
      retry: false,
    });

  const entityData = entityDataResponse?.entity_data;

  const steps = useMemo(() => {
    const step1Status = getIdentificareStatus(entityData);
    const step2Result = getEfectPerturbatorStatus(entityData);
    const step3Status = getAnalizaRiscStatus(riskData);

    return {
      step1: {
        id: 1,
        status: step1Status,
        isVisible: true,
      },
      step2: {
        id: 2,
        status: step2Result.status,
        isVisible: true, // Always visible, grayed out when not applicable
      },
      step3: {
        id: 3,
        status: step3Status,
        isVisible: true,
      },
      step4: {
        id: 4,
        status: "not_started" as GuideStepStatus,
        isVisible: true,
      },
      step5: {
        id: 5,
        status: "not_started" as GuideStepStatus,
        isVisible: true,
      },
    };
  }, [entityData, riskData]);

  const documentChecklist = useMemo(
    () => getDocumentChecklistStatus(entityData),
    [entityData]
  );

  return {
    steps,
    documentChecklist,
    isLoading: isLoadingEntity || isLoadingRisk,
    hasSession: !!sessionId,
    entityData,
    riskData,
  };
}
